/**
 * @file
 * @author chenyaqiu@baidu.com
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ChromePicker } from 'react-color';
import classNames from 'classnames';
import Select from '../select';
import Input from '../input';
import { DEFAULT_COLORS, getHexWithAlpha } from './util';
import { ConfigContext } from '../config-provider';
;
const DEFAULT_RGB = { r: 0, g: 0, b: 0, a: 0 };
const WHITE_REG = /^#(fff|ffffff)([0-9a-f]{3})/;
const HEX_REG = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})([0-9A-Fa-f]{2})?$/;
const ColorPicker = props => {
    const { defaultColor, defaultColors = DEFAULT_COLORS, disabled = false, type = 'default', color, className, style, placeholder = '请选择色值', inputPlaceholder = '请输入hex色值', prefixCls: customPrefixCls, allowClear = false, dropdownStyle = { minWidth: 240 }, onChange } = props;
    const hasColor = 'color' in props;
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('colorpicker', customPrefixCls);
    const [curColor, setCurColor] = useState(defaultColor);
    const [curRgb, setCurRgb] = useState(defaultColor === 'transparent' ? DEFAULT_RGB : defaultColor);
    const [inputVal, setInputVal] = useState();
    const isTransparent = curColor === 'transparent';
    const isWhite = !isTransparent && curColor && WHITE_REG.test(getHexWithAlpha({ hex: curColor }).toLowerCase());
    const isCustom = type === 'custom';
    const colorpickerClassName = classNames(prefixCls, { [`${prefixCls}-custom`]: isCustom }, { [`${prefixCls}-default`]: type === 'default' }, { [`${prefixCls}-simple`]: type === 'simple' }, { className });
    const handleChange = useCallback((color) => {
        setInputVal(color);
        if (!color || HEX_REG.test(color)) {
            setCurColor(color);
            setCurRgb(color);
            onChange && onChange(color);
        }
    }, [onChange]);
    const selectRender = useMemo(() => (<div className="colorpicker-dropdowns-render">
            {curColor && <div className={classNames('colorpicker-dropdowns-render-block', {
                'none': isTransparent,
                'white': isWhite
            })} style={!isTransparent && curColor ? { backgroundColor: curColor } : {}}/>}
            <span className="colorpicker-dropdowns-render-text">
                {type !== 'simple' && !isTransparent ? curColor : ''}
            </span>
        </div>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [isTransparent, curColor]);
    const dropDownRender = useMemo(() => (<>
            {isCustom && (<Input className="colorpicker-input" placeholder={inputPlaceholder} value={isTransparent ? undefined : inputVal || curColor} onChange={e => handleChange(e.target.value)}/>)}
            <div className="colorpicker-dropdowns">
                {defaultColors && defaultColors.map(color => {
            const isSelected = color === curColor;
            const isWhite = WHITE_REG.test(color.toLowerCase());
            const isTrans = color === 'transparent';
            return (<div key={color} className={classNames('colorpicker-dropdowns-color', {
                    none: isTrans,
                    white: isWhite,
                    ['colorpicker-dropdowns-color-selected']: isSelected
                })} onClick={() => {
                    curColor !== color && setCurColor(color);
                    isCustom && curColor !== color && setCurRgb(isTrans ? DEFAULT_RGB : color);
                    onChange && onChange(isTrans ? '' : color);
                }}>
                        <div className="colorpicker-dropdowns-color-block" style={!isTrans ? { backgroundColor: color } : {}}/>
                    </div>);
        })}
            </div>
        </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [isCustom, curColor, inputVal, defaultColors, onChange, isTransparent, inputPlaceholder]);
    const customDropdownRender = useMemo(() => (<div className="colorpicker-dropdowns-custom">
            <ChromePicker color={curRgb} onChangeComplete={(color) => {
            var _a;
            setCurRgb(((_a = color === null || color === void 0 ? void 0 : color.rgb) === null || _a === void 0 ? void 0 : _a.a) === 1 ? color.hex : color.rgb);
            setCurColor(getHexWithAlpha(color));
            onChange && onChange(getHexWithAlpha(color), color);
        }}/>
            {dropDownRender}
        </div>), [dropDownRender, curRgb, onChange]);
    useEffect(() => {
        if (hasColor && color !== curColor) {
            setCurColor(color);
            setCurRgb(color);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color, hasColor]);
    useEffect(() => {
        curColor && setInputVal(undefined);
    }, [curColor]);
    return (<div className={colorpickerClassName} style={style}>
            <Select dropdownClassName={`colorpicker-dropdowns-con${isCustom ? ' custom' : ''}`} mode="multiple" placeholder={placeholder} maxTagCount={1} allowClear={allowClear} disabled={disabled} tagRender={() => selectRender} dropdownRender={() => (isCustom ? customDropdownRender : dropDownRender)} dropdownStyle={dropdownStyle} value={curColor ? [curColor] : undefined} onClear={() => handleChange('')} showSearch={false}/>
        </div>);
};
export default ColorPicker;
